import { useEffect, useRef } from 'react';
import { Control, Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { fDate } from 'src/utils/format-time';

import Label from 'src/components/label';

import { IInventoryBatch } from 'src/types/inventory-batch';

type Props = {
  name: string; // e.g. `items.${index}.batchAllocations`
  batches: IInventoryBatch[]; // active batches for the selected item, FEFO-sorted
  targetQuantity: number; // the line's requested quantity to allocate
};

// Shared by kitchen and bar dispatch rows — the shape is identical for both.
export default function BatchAllocationPicker({ name, batches, targetQuantity }: Props) {
  const { control } = useFormContext();
  const typedControl = control as Control<any>;

  const { fields, replace } = useFieldArray({ control: typedControl, name });

  const isFirstRun = useRef(true);

  useEffect(() => {
    // Skip the very first auto-fill when editing an order that already has
    // allocations seeded from defaultValues — don't clobber the saved
    // selections with a fresh FEFO suggestion the instant batches load.
    const isEditModeWithExistingAllocations = isFirstRun.current && fields.length > 0;
    isFirstRun.current = false;

    if (isEditModeWithExistingAllocations) {
      return;
    }

    const defaultAllocations = batches.reduce<{ batchId: string; quantity: number }[]>(
      (allocations, batch) => {
        const remaining =
          targetQuantity - allocations.reduce((sum, allocation) => sum + allocation.quantity, 0);
        const take = Math.min(Math.max(remaining, 0), batch.quantity);
        return take > 0 ? [...allocations, { batchId: batch._id, quantity: take }] : allocations;
      },
      []
    );

    replace(defaultAllocations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches, targetQuantity]);

  const watchAllocations: { batchId: string; quantity: number }[] =
    useWatch({ control: typedControl, name }) ?? [];

  const allocatedTotal = watchAllocations.reduce(
    (sum, allocation) => sum + (Number(allocation?.quantity) || 0),
    0
  );
  const mismatch = Math.abs(allocatedTotal - targetQuantity) > 0.001;

  return (
    <Stack spacing={1} sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Allocate {targetQuantity} across batches (FEFO-suggested, editable)
      </Typography>

      {fields.map((field, allocIndex) => {
        const allocation = field as unknown as { batchId: string; quantity: number };
        const batch = batches.find((b) => b._id === allocation.batchId);

        return (
          <Stack direction="row" spacing={2} alignItems="center" key={field.id}>
            <Box sx={{ minWidth: 140 }}>
              {batch?.expiryDate ? (
                fDate(batch.expiryDate)
              ) : (
                <Label variant="soft" color="default">
                  Unknown Expiry
                </Label>
              )}
            </Box>

            <Typography variant="caption" color="text.disabled" sx={{ minWidth: 90 }}>
              avail: {batch?.quantity ?? 0}
            </Typography>

            <Controller
              name={`${name}.${allocIndex}.quantity`}
              control={typedControl}
              render={({ field: quantityField }) => (
                <TextField
                  {...quantityField}
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: batch?.quantity ?? 0 }}
                  onChange={(e) => quantityField.onChange(Number(e.target.value))}
                  sx={{ maxWidth: 120 }}
                />
              )}
            />
          </Stack>
        );
      })}

      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption" color={mismatch ? 'error.main' : 'text.secondary'}>
          Allocated {allocatedTotal} / {targetQuantity}
        </Typography>
        {mismatch && (
          <Typography variant="caption" color="error.main">
            Must add up exactly
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
